/*
  ========================================================================
  THEME: Impact v7.0.1 merge target + Gro unified rebuild
  STORES: Gro Clinics AU (groclinics.com.au) + NZ (groclinics.co.nz)
  LAST UPDATED: 11/08/2026
  PREVIOUSLY: 26/06/2026
  ========================================================================
  Impact theme technical documentation is in assets/documentation.txt.
  Custom Gro modules are documented in snippets/_gro-docs-*.liquid.
  ========================================================================
*/

/*
  QUIZ_DEV_MODE - MUST BE false IN ANY PUSHED BUILD.

  When true: skips _scoreAndFind() entirely and renders the FIRST result
  block regardless of the visitor's answers, so the result UI can be
  reviewed without completing the quiz.

  This shipped to production as true and stayed live from 26/06/2026 to
  11/08/2026 - every visitor who finished the quiz saw the same
  recommendation. Do not leave it on to "check something quickly": the
  quiz is a lead-capture path on a medical-adjacent site, and a wrong
  recommendation is a clinical-trust problem, not a cosmetic one.

  Turn it on locally only, and turn it off in the same sitting.
*/
const QUIZ_DEV_MODE = false;

class GroQuiz {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;

    this.answers = {};
    this.currentStep = 0;
    this.navigating = false;

    this.config = this._parseConfig();
    this.steps = this.config.steps || [];
    this.totalSteps = this.steps.length;

    this.stepsEl = container.querySelector(`#gro-quiz-steps-${this.sectionId}`);
    this.progressEl = container.querySelector('.gro-quiz__progress');
    this.progressBarEl = container.querySelector('.gro-quiz__progress-bar');
    this.emailStepEl = container.querySelector(`#gro-quiz-email-${this.sectionId}`);
    this.resultsEl = container.querySelector(`#gro-quiz-results-${this.sectionId}`);
    this.resultsGridEl = container.querySelector(`#gro-quiz-results-grid-${this.sectionId}`);

    if (this.totalSteps > 0) this._init();
  }

  /* ---- Config: read from DOM (built by Liquid from section.blocks) ---- */

  _parseConfig() {
    const configEl = this.container.querySelector('.gro-quiz__config');
    if (!configEl) return { steps: [] };

    const steps = [...configEl.querySelectorAll('.gro-quiz__step-def')].map((stepEl) => ({
      key: stepEl.dataset.key,
      question: stepEl.dataset.question,
      options: [...stepEl.querySelectorAll('.gro-quiz__option-def')].map((optEl) => ({
        label: optEl.dataset.label,
        value: optEl.dataset.value,
        recommendation: optEl.dataset.recommendation || '',
      })),
    }));

    return { steps };
  }

  _init() {
    this._renderStep(0);
    this._bindEmailCapture();
  }

  /* ---- Step rendering ---- */

  _renderStep(index) {
    const step = this.steps[index];
    if (!step) return;

    this._updateProgress(index);

    const isLast = index === this.totalSteps - 1;
    const selected = this.answers[step.key]?.value;
    const questionId = `gro-quiz-q-${this.sectionId}-${index}`;

    const stepEl = document.createElement('div');
    stepEl.className = 'gro-quiz__step';
    stepEl.dataset.stepIndex = index;

    stepEl.innerHTML = `
      <h3 id="${questionId}" class="gro-quiz__question" tabindex="-1">${step.question}</h3>
      <div class="gro-quiz__options" role="radiogroup" aria-labelledby="${questionId}">
        ${step.options.map((opt) => `
          <button
            class="gro-quiz__option${selected === opt.value ? ' is-selected' : ''}"
            role="radio"
            aria-checked="${selected === opt.value ? 'true' : 'false'}"
            tabindex="${selected === opt.value ? '0' : (step.options.indexOf(opt) === 0 && !selected ? '0' : '-1')}"
            data-value="${opt.value}"
            data-recommendation="${opt.recommendation}"
            data-step-key="${step.key}"
          >${opt.label}</button>
        `).join('')}
      </div>
      <div class="gro-quiz__nav">
        ${index > 0
          ? `<button class="gro-quiz__btn gro-quiz__btn--ghost js-gro-quiz-back" aria-label="Back to previous question">Back</button>`
          : '<span></span>'
        }
        <button
          class="gro-quiz__btn gro-quiz__btn--primary js-gro-quiz-next"
          ${!selected ? 'disabled' : ''}
          aria-label="${isLast ? 'See my recommendation' : 'Next question'}"
        >${isLast ? 'See my recommendation' : 'Next'}</button>
      </div>
    `;

    this.stepsEl.innerHTML = '';
    this.stepsEl.appendChild(stepEl);
    this._bindStepEvents(stepEl, step);

    if (this.navigating) {
      stepEl.querySelector('.gro-quiz__question').focus();
    }
    this.navigating = true;
  }

  _bindStepEvents(stepEl, step) {
    const options = [...stepEl.querySelectorAll('.gro-quiz__option')];

    options.forEach((btn, i) => {
      btn.addEventListener('click', () => this._selectOption(btn, options));
      btn.addEventListener('keydown', (e) => this._handleOptionKey(e, btn, options, i));
    });

    stepEl.querySelector('.js-gro-quiz-back')?.addEventListener('click', () => this._goBack());
    stepEl.querySelector('.js-gro-quiz-next')?.addEventListener('click', () => this._goNext());
  }

  _handleOptionKey(e, btn, options, index) {
    const last = options.length - 1;
    let target = null;

    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      target = options[index < last ? index + 1 : 0];
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      target = options[index > 0 ? index - 1 : last];
    } else if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      this._selectOption(btn, options);
      return;
    }

    if (target) {
      options.forEach((o) => o.setAttribute('tabindex', '-1'));
      target.setAttribute('tabindex', '0');
      target.focus();
    }
  }

  _selectOption(btn, allOptions) {
    const key = btn.dataset.stepKey;
    const value = btn.dataset.value;
    const recommendation = btn.dataset.recommendation;

    this.answers[key] = { value, recommendation };

    allOptions.forEach((o) => {
      o.classList.remove('is-selected');
      o.setAttribute('aria-checked', 'false');
      o.setAttribute('tabindex', '-1');
    });

    btn.classList.add('is-selected');
    btn.setAttribute('aria-checked', 'true');
    btn.setAttribute('tabindex', '0');

    const nextBtn = this.stepsEl.querySelector('.js-gro-quiz-next');
    if (nextBtn) nextBtn.disabled = false;
  }

  _goNext() {
    if (this.currentStep < this.totalSteps - 1) {
      this.currentStep++;
      this._renderStep(this.currentStep);
    } else {
      this._completeQuiz();
    }
  }

  _goBack() {
    if (this.currentStep > 0) {
      this.currentStep--;
      this._renderStep(this.currentStep);
    }
  }

  /* ---- Progress ---- */

  _updateProgress(stepIndex) {
    const pct = Math.round((stepIndex / this.totalSteps) * 100);
    this.progressBarEl.style.width = `${pct}%`;
    this.progressEl.setAttribute('aria-valuenow', String(pct));
  }

  /* ---- Completion ---- */

  _completeQuiz() {
    this._updateProgress(this.totalSteps);
    this.stepsEl.hidden = true;

    const recommendations = Object.values(this.answers).map((a) => a.recommendation);

    document.dispatchEvent(
      new CustomEvent('quiz:complete', {
        bubbles: true,
        detail: {
          answers: this.answers,
          recommendations,
        },
      })
    );

    if (this.emailStepEl) {
      this.emailStepEl.hidden = false;
      this.emailStepEl.querySelector('input[type="email"]')?.focus();
    } else {
      this._showResult();
    }
  }

  /* ---- Email capture ---- */

  _bindEmailCapture() {
    if (!this.emailStepEl) return;

    const form = this.emailStepEl.querySelector('.gro-quiz__email-form');
    const skipBtn = this.emailStepEl.querySelector('.js-gro-quiz-skip-email');

    form?.addEventListener('submit', (e) => {
      e.preventDefault();
      this._submitEmail(form);
    });

    skipBtn?.addEventListener('click', () => {
      this.emailStepEl.hidden = true;
      this._showResult();
    });
  }

  _submitEmail(form) {
    const recommendations = Object.values(this.answers).map((a) => a.recommendation);

    const noteInput = document.createElement('input');
    noteInput.type = 'hidden';
    noteInput.name = 'contact[note]';
    noteInput.value = `Quiz answers: ${JSON.stringify(this.answers)} | Recommendations: ${recommendations.join(', ')}`;
    form.appendChild(noteInput);

    fetch('/contact', {
      method: 'POST',
      body: new FormData(form),
      headers: { Accept: 'application/json' },
    }).catch(() => {
      /* Non-blocking */
    }).finally(() => {
      this.emailStepEl.hidden = true;
      this._showResult();
    });
  }

  /* ---- Scoring and result rendering ---- */

  _showResult() {
    this.resultsEl.hidden = false;
    this.resultsEl.focus();

    if (QUIZ_DEV_MODE) {
      console.info('[QUIZ_DEV_MODE] Showing first result block. Set QUIZ_DEV_MODE = false before pushing.');
      const firstResult = this.container.querySelector('.gro-quiz__result-def');
      this._renderResults(firstResult ? [firstResult] : []);
      return;
    }

    const winnerDefs = this._scoreAndFind();
    this._renderResults(winnerDefs);
  }

  _scoreAndFind() {
    /* Tally a point per answer towards its recommendation */
    const scores = {};
    Object.values(this.answers).forEach(({ recommendation }) => {
      if (recommendation) {
        scores[recommendation] = (scores[recommendation] || 0) + 1;
      }
    });

    if (!Object.keys(scores).length) {
      /* No recommendations mapped - show all result blocks */
      return [...this.container.querySelectorAll('.gro-quiz__result-def')];
    }

    const maxScore = Math.max(...Object.values(scores));
    const winners = Object.keys(scores).filter((k) => scores[k] === maxScore);

    const allResultDefs = [...this.container.querySelectorAll('.gro-quiz__result-def')];
    const matched = allResultDefs.filter((def) => winners.includes(def.dataset.key));

    /* Fall back to all results if nothing matched */
    return matched.length ? matched : allResultDefs;
  }

  _renderResults(resultDefs) {
    this.resultsGridEl.innerHTML = '';

    if (!resultDefs.length) {
      this.resultsGridEl.innerHTML = '<p class="gro-quiz__no-results">No recommendation found - please contact us directly.</p>';
      return;
    }

    resultDefs.forEach((def) => {
      this.resultsGridEl.appendChild(this._buildResultCard(def));
    });
  }

  _buildResultCard(def) {
    const title = def.dataset.title || '';
    const ctaLabel = def.dataset.ctaLabel || 'Book a free consultation';
    const ctaUrl = def.dataset.ctaUrl || '#';
    const imgSrc = def.dataset.image || '';
    const imgAlt = def.dataset.imageAlt || title;
    const descEl = def.querySelector('.gro-quiz__result-desc-src');
    const description = descEl?.textContent?.trim() || '';

    const card = document.createElement('div');
    card.className = 'gro-quiz-result';

    card.innerHTML = `
      ${imgSrc ? `
        <div class="gro-quiz-result__image-wrap">
          <img
            src="${imgSrc}"
            alt="${imgAlt}"
            class="gro-quiz-result__image"
            width="900"
            loading="lazy"
          >
        </div>` : ''
      }
      <div class="gro-quiz-result__body">
        ${title ? `<h3 class="gro-quiz-result__title">${title}</h3>` : ''}
        ${description ? `<p class="gro-quiz-result__description">${description}</p>` : ''}
        <a
          href="${ctaUrl}"
          class="gro-quiz__btn gro-quiz__btn--primary gro-quiz-result__cta"
        >${ctaLabel}</a>
      </div>
    `;

    return card;
  }
}

/* ---- Bootstrap ---- */
document.querySelectorAll('.gro-quiz').forEach((el) => new GroQuiz(el));
